import { connect, ConnectedProps } from 'react-redux';
import { useLocation } from 'react-router';
import { Action, Dispatch } from 'redux';

import lang from '../../lib/language';
import { PurchasePaymentTypeEnum } from '../../lib/models/crm/types';
import { RootState } from '../../lib/store';
import { hasAnyAccountPaidPlan } from '../../lib/store/crm/helpers';

import WarningIcon from '../../assets/img/warning-icon.svg';
import { UI_ROUTES } from '../../util/routes';

import styles from './AwaitingPaymentNotification.module.scss';

type OwnProps = {
  isSolanaPendingPlanPayment?: boolean;
}

const PLAN_PURCHASE_TYPES = [
  PurchasePaymentTypeEnum.Plan,
  PurchasePaymentTypeEnum.PlanWithAutoPass,
];

const AwaitingPaymentNotification = ({
  isSolanaPaymentPending,
  pendingSolanaPayments,
  totalLossReached,
  isRewindProcessing,
  hasPaidPlan,
  isSolanaPendingPlanPayment,
}: ReduxProps & OwnProps) => {
  const location = useLocation();
  const isPlanDetailsScreen = location.pathname.includes(UI_ROUTES.planDetails);

  if (isSolanaPendingPlanPayment) {
    return (
      <div className={styles.container} id="awaitingPaymentNotification">
        <div className={styles.content}>
          <img className={styles.warningIcon} src={WarningIcon} alt="Warning" />
          <div>{lang.awaitingPaymentNotificationText()}</div>
        </div>
      </div>
    );
  }

  // On PlanDetails show the AutoPass pending payment; on other screens show the non-AutoPass one
  const relevantPayment = isPlanDetailsScreen
    ? pendingSolanaPayments.find(p => p.purchase_type_id === PurchasePaymentTypeEnum.AutoPass)
    : pendingSolanaPayments.find(p => p.purchase_type_id !== PurchasePaymentTypeEnum.AutoPass
        && p.purchase_type_id !== PurchasePaymentTypeEnum.AccountExpiryExtension);

  const purchaseType: PurchasePaymentTypeEnum | undefined = relevantPayment?.purchase_type_id;

  if (isSolanaPaymentPending !== true || !relevantPayment || !purchaseType) {
    return null;
  }

  if (PLAN_PURCHASE_TYPES.includes(purchaseType) && hasPaidPlan && !totalLossReached) {
    return null;
  }

  if (purchaseType === PurchasePaymentTypeEnum.AccountDailyLossRewind && isRewindProcessing) {
    return null;
  }

  const renderText = () => {
    if (purchaseType === PurchasePaymentTypeEnum.AutoPass) {
      return lang.awaitingPaymentNotificationText3();
    }
    if (purchaseType === PurchasePaymentTypeEnum.AccountDailyLossRewind) {
      return lang.awaitingPaymentNotificationText2();
    }

    return lang.awaitingPaymentNotificationText();
  };

  return (
    <div className={styles.container} id="awaitingPaymentNotification">
      <div className={styles.content}>
        <img className={styles.warningIcon} src={WarningIcon} alt="Warning" />
        <div>{renderText()}</div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isSolanaPaymentPending: state.payment.isSolanaPaymentPending,
  pendingSolanaPayments: state.payment.pendingSolanaPayments,
  totalLossReached: state.closeout.accountStatusData.totalLossReached,
  isRewindProcessing: state.closeout.accountStatusData.dailyResetProcessing,
  hasPaidPlan: hasAnyAccountPaidPlan(state.crm),
});

const mapDispatchToProps = (_dispatch: Dispatch<Action>) => ({

});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(AwaitingPaymentNotification);
